import { DateTime } from '@ksyos/date-time';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { Osasc2Policy } from '../../../../../../common/customControls/osasc2Policy';
import { GradingSummary } from '../../../../../../common/enums/gradingSummary';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, InputRadio, NullableInputText } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { DateField } from '../../../../fields/Date';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { TextAreaField } from '../../../../fields/TextArea';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { WorkingDiagnosis } from './workingDiagnosis';

interface ICustomControlData {
    dateConsult?: string | null;
    policy: string | null;
    remarks: string | null;
    workingDiagnosis: string | null;
}

interface IInnerOsasc2V1DiagnosticReportAddEditState {
    buttonDisabled?: boolean;
}

export function Osasc2V1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    return <InnerOsasc2V1DiagnosticReportAddEdit {...props} />;
}

export class InnerOsasc2V1DiagnosticReportAddEdit extends Component<
    ICustomControlDiagnosticReportAddEditProps,
    IInnerOsasc2V1DiagnosticReportAddEditState
> {
    public render() {
        const workingDiagnosisOptions = [
            WorkingDiagnosis.lichtOSAS,
            WorkingDiagnosis.matigOSAS,
            WorkingDiagnosis.ernstigOSAS,
            WorkingDiagnosis.geenOSAS,
        ];
        const policyOptions = [
            Osasc2Policy.mandibulairRepositieApparaat,
            Osasc2Policy.continuousPositiveAirwayPressure,
            Osasc2Policy.sleepPositionTrainer,
        ];

        return (
            <ProcessContainer>
                <ProcessItemHeader
                    isFirstItem
                    isItemOpen
                    organizationName={this.props.currentDiagnosticReport?.performerOrganization?.name}
                    practitionerName={
                        this.props.currentDiagnosticReport && this.props.currentDiagnosticReport.performerPractitioner
                            ? getDisplayName(this.props.currentDiagnosticReport.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    serviceType={ServiceType.consultation}
                    title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
                />

                <Form onSubmit={() => null}>
                    <FormContextProvider
                        value={{
                            placeholderGroupName: 'diagnosticReport',
                            item: this.props.diagnosticReport.customControlData,
                            errors: this.props.errors,
                            onChange: async (key, value) => await this.handleChangeCustomControl({ [key]: value }),
                            onDoValidate: async (key) => await this.validateOneCustomControl(key),
                        }}
                    >
                        <DateField
                            error={this.props.errors.get('customControlData.dateConsult')}
                            fieldKey="dateConsult"
                            hasErrors={this.props.errors.has('customControlData.dateConsult')}
                            isRequired
                            label={translateKey('diagnosticReport.dateConsult')}
                            maxDate={DateTime.now().format()}
                            placeholder={`${translateKey('common.date')}...`}
                            size="medium"
                        />
                        <TextAreaField
                            automation="osasFindings"
                            error={this.props.errors.get('customControlData.remarks')}
                            fieldKey="remarks"
                            hasErrors={this.props.errors.has('customControlData.remarks')}
                            isRequired
                            label={translateKey('diagnosticReport.findings')}
                            placeholder="Conclusie van het slaaprapport en gesprek met de patiënt..."
                        />
                        <RadioGroupField
                            automation="osasWorkingDiagnosis"
                            customOption={
                                <InputRadio
                                    label={
                                        <NullableInputText
                                            onChange={async (value) =>
                                                await this.handleChangeCustomControl({ workingDiagnosis: value })
                                            }
                                            onDoValidate={async () =>
                                                await this.validateOneCustomControl('workingDiagnosis')
                                            }
                                            placeholder="Anders, namelijk..."
                                            value={
                                                this.props.diagnosticReport.customControlData.workingDiagnosis &&
                                                workingDiagnosisOptions.indexOf(
                                                    this.props.diagnosticReport.customControlData
                                                        .workingDiagnosis as WorkingDiagnosis
                                                ) === -1
                                                    ? this.props.diagnosticReport.customControlData.workingDiagnosis
                                                    : null
                                            }
                                        />
                                    }
                                    name={'workingDiagnosis'}
                                    onChange={async (value) =>
                                        await this.handleChangeCustomControl({ workingDiagnosis: value ? value : '' })
                                    }
                                    onValue={''}
                                    value={
                                        this.props.diagnosticReport.customControlData.workingDiagnosis === null ||
                                        workingDiagnosisOptions.indexOf(
                                            this.props.diagnosticReport.customControlData
                                                .workingDiagnosis as WorkingDiagnosis
                                        ) !== -1
                                            ? null
                                            : ''
                                    }
                                />
                            }
                            error={this.props.errors.get('customControlData.workingDiagnosis')}
                            fieldKey="workingDiagnosis"
                            hasErrors={this.props.errors.has('customControlData.workingDiagnosis')}
                            isRequired
                            labels={workingDiagnosisOptions}
                            options={workingDiagnosisOptions}
                            radioButtonsStacked={true}
                        />
                        <RadioGroupField
                            automation="osasPolicy"
                            customOption={
                                <InputRadio
                                    label={
                                        <NullableInputText
                                            onChange={async (value) =>
                                                await this.handleChangeCustomControl({ policy: value })
                                            }
                                            onDoValidate={async () => await this.validateOneCustomControl('policy')}
                                            placeholder="Anders, namelijk..."
                                            value={
                                                this.props.diagnosticReport.customControlData.policy &&
                                                policyOptions.indexOf(
                                                    this.props.diagnosticReport.customControlData.policy as Osasc2Policy
                                                ) === -1
                                                    ? this.props.diagnosticReport.customControlData.policy
                                                    : null
                                            }
                                        />
                                    }
                                    name={'policy'}
                                    onChange={async (value) =>
                                        await this.handleChangeCustomControl({ policy: value ? value : '' })
                                    }
                                    onValue={''}
                                    value={
                                        this.props.diagnosticReport.customControlData.policy === null ||
                                        policyOptions.indexOf(
                                            this.props.diagnosticReport.customControlData.policy as Osasc2Policy
                                        ) !== -1
                                            ? null
                                            : ''
                                    }
                                />
                            }
                            error={this.props.errors.get('customControlData.policy')}
                            fieldKey="policy"
                            hasErrors={this.props.errors.has('customControlData.policy')}
                            isRequired
                            labels={policyOptions}
                            options={policyOptions}
                            radioButtonsStacked={true}
                        />
                    </FormContextProvider>
                    <FormContextProvider
                        value={{
                            placeholderGroupName: 'diagnosticReport',
                            item: this.props.diagnosticReport,
                            errors: this.props.errors,
                            onChange: async (key, value) => await this.props.handleChange(key, value),
                            onDoValidate: async (key) => await this.props.validateOne(key),
                        }}
                    >
                        <TextAreaField
                            error={this.props.errors.get('diagnosisRemarks')}
                            fieldKey="diagnosisRemarks"
                            hasErrors={this.props.errors.has('diagnosisRemarks')}
                            label={translateKey('common.remarks')}
                            placeholder="Een toelichting, opmerkingen of vragen..."
                        />
                    </FormContextProvider>

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.diagnosticReport}
                                isExisting={!!this.props.request.id}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveDiagnosticReport('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveDiagnosticReport('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </ProcessContainer>
        );
    }

    private async handleChangeCustomControl(value: Partial<ICustomControlData>) {
        await this.props.handleChangeCustomControl(value);
    }

    private async validateOneCustomControl(name: keyof ICustomControlData) {
        await this.props.validateOneCustomControl(name);
    }

    private async saveDiagnosticReport(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedDiagnosticReport = await this.props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const diagnosticReportId = await this.props.onSaveDiagnosticReport(saveType);

        const result = await fetchJson(`/api/diagnosticReports/${diagnosticReportId}`, 'GET');
        if (saveType === 'final') {
            if (result.json.gradingSummary === GradingSummary.deviant) {
                showServiceToast(
                    ToastType.success,
                    getServiceLabel(this.props.service.code, 'deviantAlert', this.props.service.name),
                    this.props.service,
                    'request',
                    translateKey(
                        'toast.answerSentToOrganizationSuccess',
                        this.props.request.requesterOrganization?.name
                    )
                );
            } else {
                showServiceToast(
                    ToastType.success,
                    'toast.submitSuccess',
                    this.props.service,
                    'request',
                    translateKey(
                        'toast.answerSentToOrganizationSuccess',
                        this.props.request.requesterOrganization?.name
                    )
                );
            }
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await this.setState({ buttonDisabled: false });
        await this.props.onSave(diagnosticReportId);
    }
}

export function osasc2V1DiagnosticReportInitialize() {
    return {
        dateConsult: null,
        remarks: null,
        workingDiagnosis: null,
        policy: null,
    };
}
